import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Mail, MapPin, Phone, Printer, Save, Share2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../lib/apiClient";
import { Modal } from "./common";
import { LogoUploader } from "./LogoUploader";
import { COLORS, backHtml, cardHtml, openPrint } from "../lib/businessCard";
import { cardPng, shareCard } from "../lib/cardImage";
import { StoryDesigner } from "./StoryDesigner";

const FIELDS = [["name", "Ad Soyad"], ["title", "Unvan"], ["phone", "Telefon"], ["email", "E-posta"], ["address", "Adres"]];

function BranchPicker({ all, sel, setSel }) {
  const toggle = (b) => setSel((x) => (x.includes(b) ? x.filter((y) => y !== b) : [...x, b]));
  return (
    <div className="bcard-branches" data-testid="bcard-branches">
      {all.map((b) => (
        <button type="button" key={b} className={`chip ${sel.includes(b) ? "chip-active" : ""}`} onClick={() => toggle(b)} data-testid={`bcard-branch-${b}`}>{b}</button>
      ))}
    </div>
  );
}

function ColorPicker({ color, setColor }) {
  return (
    <div className="bcard-colors" data-testid="bcard-colors">
      {COLORS.map((c) => (
        <button type="button" key={c} className={`bcard-swatch ${color === c ? "on" : ""}`} style={{ background: c }} onClick={() => setColor(c)} title={c} data-testid={`bcard-color-${c.slice(1)}`} />
      ))}
      <label className="bcard-custom" title="Özel renk"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="bcard-color-custom" /> Özel</label>
    </div>
  );
}

function LogoSizer({ size, setSize, withBack }) {
  const row = (k, label) => (
    <label className="logo-sizer" key={k}>
      <span>{label}</span>
      <input type="range" min={60} max={150} step={5} value={size[k]} onChange={(e) => setSize((s) => ({ ...s, [k]: Number(e.target.value) }))} data-testid={`bcard-logo-size-${k}`} />
      <b data-testid={`bcard-logo-size-${k}-value`}>%{size[k]}</b>
    </label>
  );
  return <div className="logo-sizers" data-testid="bcard-logo-sizers">{row("front", "Ön yüz logo")}{withBack && row("back", "Arka yüz logo")}</div>;
}

function CardFront({ f, logo, color, qrRef, qrUrl }) {
  return (
    <div className="bcard" style={{ borderLeftColor: color }} data-testid="bcard-preview">
      <div className="bcard-left">
        {logo ? <img src={logo} alt="" className="bcard-logo" data-testid="bcard-logo" /> : <span className="bcard-mono" style={{ background: color }}>{(f.name || "S").slice(0, 1)}</span>}
        <strong data-testid="bcard-name">{f.name || "Ad Soyad"}</strong>
        <em style={{ color }}>{f.title}</em>
        <div className="bcard-lines">
          {f.phone && <span><Phone size={9} /> {f.phone}</span>}
          {f.email && <span><Mail size={9} /> {f.email}</span>}
          {f.address && <span><MapPin size={9} /> {f.address}</span>}
        </div>
      </div>
      <div className="bcard-right">
        <div ref={qrRef} className="bcard-qr">{qrUrl && <QRCodeSVG value={qrUrl} size={92} level="M" bgColor="#ffffff" fgColor="#0f172a" />}</div>
        <small style={{ color }}>Online teklif al</small>
      </div>
    </div>
  );
}

function CardBack({ f, logo, color, sel }) {
  return (
    <div className="bcard bcard-back" style={{ background: color }} data-testid="bcard-back-preview">
      {logo ? <img src={logo} alt="" className="bcard-back-logo" /> : <strong>{f.name}</strong>}
      <small>SİGORTA HİZMETLERİMİZ</small>
      <div className="bcard-back-list">{sel.map((b) => <span key={b}>{b}</span>)}</div>
    </div>
  );
}

export function BusinessCardModal({ qrUrl: qrProp, onBack, onClose }) {
  const [f, setF] = useState({ name: "", title: "", phone: "", email: "", address: "" });
  const [logo, setLogo] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState([]);
  const [withBack, setWithBack] = useState(true);
  const [qrUrl, setQrUrl] = useState(qrProp || "");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const qrRef = useRef(null);
  const loadPrefs = useCallback(() => axios.get(`${API}/user/preferences`).then(({ data }) => {
    const s = data.signature || {};
    setF({ name: s.name || "", title: s.title || "", phone: s.phone || "", email: s.email || "", address: s.address || "" });
    setLogo(data.logoUrl || "");
    setSize({ front: data.cardLogoFront || 100, back: data.cardLogoBack || 100 });
  }), []);
  const [size, setSize] = useState({ front: 100, back: 100 });
  const firstSize = useRef(true);
  useEffect(() => {
    if (!loaded) return undefined;
    if (firstSize.current) { firstSize.current = false; return undefined; }
    const t = setTimeout(() => axios.put(`${API}/user/preferences`, { cardLogoFront: size.front, cardLogoBack: size.back }).catch(() => {}), 600);
    return () => clearTimeout(t);
  }, [size, loaded]);
  const fs = size.front / 100, bs = size.back / 100;
  useEffect(() => {
    const jobs = [loadPrefs(), axios.get(`${API}/branches`).then(({ data }) => { const n = (data || []).map((b) => b.name).filter(Boolean); setAll(n); setSel(n); })];
    if (!qrProp) jobs.push(axios.get(`${API}/me/public-quote-link`).then(({ data }) => setQrUrl(`${window.location.origin}/q/${data.slug}?src=qr`)));
    Promise.allSettled(jobs).then(() => setLoaded(true));
  }, [qrProp, loadPrefs]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, { signatureName: f.name, signatureTitle: f.title, signaturePhone: f.phone, signatureEmail: f.email, signatureAddress: f.address });
      toast.success("Bilgiler kaydedildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); } finally { setSaving(false); }
  };
  const print = (sheet) => openPrint({ front: cardHtml({ ...f, logo, qr: qrRef.current?.innerHTML || "" }), back: withBack && sel.length ? backHtml({ name: f.name, logo, branches: all.filter((b) => sel.includes(b)) }) : "", color, sheet, fs, bs });
  const ordered = all.filter((b) => sel.includes(b));
  const [busy, setBusy] = useState("");
  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const { data } = await axios.post(`${API}/business-card/pdf`, { ...f, color, qrUrl, branches: withBack ? ordered : [], logoFront: fs, logoBack: bs }, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = "kartvizit-matbaa.pdf";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("PDF oluşturulamadı"); } finally { setBusy(""); }
  };
  const share = async () => {
    setBusy("share");
    try {
      const blob = await cardPng({ f, logo, color, qrSvg: qrRef.current?.innerHTML || "", ls: fs });
      const r = await shareCard(blob, `Online teklif almak için QR kodu okutun: ${qrUrl}`);
      if (r === "downloaded") toast.success("Görsel indirildi; WhatsApp veya Instagram'da paylaşabilirsiniz");
    } catch { toast.error("Görsel oluşturulamadı"); } finally { setBusy(""); }
  };
  return (
    <Modal title="QR Kartvizit" onClose={onClose}>
      <div className="bcard-modal" data-testid="business-card-modal">
        {onBack && <button type="button" className="text-button" onClick={onBack} data-testid="bcard-back"><ArrowLeft size={14} /> Online linke dön</button>}
        <div style={{ "--ls": fs, "--bls": bs, display: "contents" }}>
          <CardFront f={f} logo={logo} color={color} qrRef={qrRef} qrUrl={qrUrl} />
          {withBack && ordered.length > 0 && <CardBack f={f} logo={logo} color={color} sel={ordered} />}
        </div>
        <h4 className="bcard-h">Kart rengi</h4>
        <ColorPicker color={color} setColor={setColor} />
        <h4 className="bcard-h">Logo</h4>
        <LogoUploader prefs={{ logoUrl: logo }} onChange={loadPrefs} />
        {logo && <LogoSizer size={size} setSize={setSize} withBack={withBack} />}
        <small className="muted">Logo e-posta imzanızda ve teklif PDF'lerinde de kullanılır.</small>
        <label className="prefs-row bcard-h"><input type="checkbox" checked={withBack} onChange={(e) => setWithBack(e.target.checked)} data-testid="bcard-with-back" /> <span>Arka yüz bastır (branş listesi)</span></label>
        {withBack && <BranchPicker all={all} sel={sel} setSel={setSel} />}
        <div className="form-grid bcard-form">
          {FIELDS.map(([k, l]) => (
            <label key={k} className={`field ${k === "address" ? "bcard-full" : ""}`}><span>{l}</span>
              <input value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`bcard-input-${k}`} /></label>
          ))}
        </div>
        <p className="muted small">QR kodu talepleri "QR" olarak işaretler. Çift taraflı baskıda yazıcınızda "kısa kenardan çevir" seçin.</p>
        <div className="modal-actions bcard-actions">
          <button type="button" className="outline-button" onClick={save} disabled={saving} data-testid="bcard-save"><Save size={14} /> {saving ? "Kaydediliyor…" : "Bilgileri kaydet"}</button>
          <button type="button" className="outline-button" onClick={() => print(false)} disabled={!loaded} data-testid="bcard-print-single"><Printer size={14} /> Tek kart (85×55 mm)</button>
          <button type="button" className="primary-button" onClick={() => print(true)} disabled={!loaded} data-testid="bcard-print-sheet"><Printer size={14} /> 10'lu A4 sayfa</button>
          <button type="button" className="outline-button" onClick={downloadPdf} disabled={!loaded || !!busy} data-testid="bcard-download-pdf"><FileDown size={14} /> {busy === "pdf" ? "Hazırlanıyor…" : "Matbaa PDF (3 mm taşma)"}</button>
          <button type="button" className="primary-button bcard-share" onClick={share} disabled={!loaded || !!busy} data-testid="bcard-share"><Share2 size={14} /> {busy === "share" ? "Hazırlanıyor…" : "WhatsApp / Instagram'da paylaş"}</button>
        </div>
        <StoryDesigner f={f} logo={logo} color={color} qrRef={qrRef} qrUrl={qrUrl} branches={all} disabled={!loaded} ls={fs} />
      </div>
    </Modal>
  );
}
